import { Modal, Form, Input, Button, message } from "antd";
import { useState, useEffect, useRef, KeyboardEvent, ChangeEvent, ClipboardEvent } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./RequestAccessModal.scss";
import "./OtpStep.scss";
import {
  buildApiUrl,
  BOARD_REQUEST_SEND_OTP_URL,
  BOARD_REQUEST_SUBMIT_URL,
  BOARD_REQUEST_RESEND_OTP_URL,
} from "../../../utils/constants";
import { useTurnstile } from "../../../utils/useTurnstile";

interface RequestAccessModalProps {
  open: boolean;
  onClose: () => void;
}

type ModalStep = "form" | "otp";

type BoardRequestPayload = {
  organizationName: string;
  email: string;
  phoneNumber: string;
  description: string;
};

const PhoneInputWrapper = ({ value, onChange, ...props }: any) => {
  return (
    <PhoneInput
      {...props}
      value={value}
      onChange={(phone) => {
        onChange?.(phone);
      }}
    />
  );
};

const normalizeApiMessage = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map(String).join(" ");
  }
  if (typeof value === "string") {
    return value;
  }
  return value == null ? "" : String(value);
};

const formatApiErrorMessage = (messageText: unknown): string => {
  const text = normalizeApiMessage(messageText).trim();
  const withFieldLabel = text.replace(/^description\b/i, "Description");
  return withFieldLabel.replace(
    /^Description must be longer than or equal to 10 characters$/i,
    "Description must be at least 10 characters",
  );
};

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

// react-phone-input-2 hands back bare digits ("919876543210"); the API expects
// them in E.164 form.
const toE164 = (phone: string): string =>
  phone.startsWith("+") ? phone : `+${phone}`;

const formatCountdown = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const RequestAccessModal = ({ open, onClose }: RequestAccessModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<ModalStep>("form");
  const [pendingRequest, setPendingRequest] =
    useState<BoardRequestPayload | null>(null);
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(
    null,
  );
  const [expiresIn, setExpiresIn] = useState(0);
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const { containerRef: captchaRef, getToken: getCaptchaToken } =
    useTurnstile();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setLoading(false);
      setStep("form");
      setPendingRequest(null);
      setOtp("");
      setShowOtp(false);
      setOtpError("");
      setResendTimer(0);
      setRemainingAttempts(null);
      setExpiresIn(0);
    }
  }, [open, form]);

  useEffect(() => {
    if (step === "otp") {
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 0);
    }
  }, [step]);

  useEffect(() => {
    if (resendTimer <= 0) return;

    const timerId = window.setInterval(() => {
      setResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [resendTimer]);

  useEffect(() => {
    if (expiresIn <= 0) return;

    const timerId = window.setInterval(() => {
      setExpiresIn((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [expiresIn]);

  // Runs the invisible Turnstile challenge and returns a token, or null (after
  // showing its own toast) if the challenge itself failed — kept separate from
  // the API call's try/catch so a captcha failure isn't reported as an API failure.
  const resolveCaptchaToken = async (): Promise<string | null> => {
    try {
      return await getCaptchaToken();
    } catch (error: any) {
      message.error(
        error?.message || "Captcha verification failed. Please try again.",
      );
      return null;
    }
  };

  const applyOtpMeta = (data: any) => {
    setRemainingAttempts(data?.data?.remainingAttempts ?? null);
    setExpiresIn(data?.data?.expiresIn ?? 0);
  };

  const handleSendOtp = async (values: any) => {
    const payload: BoardRequestPayload = {
      organizationName: values.organizationName,
      email: values.email,
      phoneNumber: toE164(values.phoneNumber),
      description: values.description,
    };

    setLoading(true);
    try {
      const captchaToken = await resolveCaptchaToken();
      if (!captchaToken) return;

      const response = await fetch(buildApiUrl(BOARD_REQUEST_SEND_OTP_URL), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: payload.email,
          captchaToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          formatApiErrorMessage(data.message || "Failed to send OTP"),
        );
      }

      // Snapshot the form so the submit call sends exactly the details the OTP
      // was issued against, even if the user edits the form behind the OTP step.
      setPendingRequest(payload);
      applyOtpMeta(data);
      setOtp("");
      setStep("otp");
      setResendTimer(RESEND_COOLDOWN_SECONDS);
      message.success(
        data.message || "OTP sent successfully to your email address.",
      );
    } catch (error: any) {
      message.error(
        formatApiErrorMessage(
          error.message || "Failed to send OTP. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const updateOtpDigit = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const chars = otp.padEnd(OTP_LENGTH, " ").split("").slice(0, OTP_LENGTH);
    chars[index] = digit || " ";
    const nextOtp = chars.join("").replace(/\s/g, "").slice(0, OTP_LENGTH);
    setOtp(nextOtp);
    setOtpError("");

    if (digit && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }

    if (event.key === "Enter") {
      event.preventDefault();
      handleVerifyOtp();
    }
  };

  const handleOtpPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;

    setOtp(pasted);
    setOtpError("");
    otpInputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== OTP_LENGTH) {
      setOtpError("Please enter the full 6-digit OTP.");
      return;
    }
    if (!pendingRequest) return;

    setOtpError("");
    setLoading(true);
    try {
      const captchaToken = await resolveCaptchaToken();
      if (!captchaToken) return;

      const response = await fetch(buildApiUrl(BOARD_REQUEST_SUBMIT_URL), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...pendingRequest,
          otp,
          captchaToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          formatApiErrorMessage(data.message || "Invalid or expired OTP"),
        );
      }

      message.success(
        data.message ||
          "Request submitted successfully! We'll get back to you soon.",
      );
      onClose();
    } catch (error: any) {
      message.error(
        formatApiErrorMessage(
          error.message || "OTP verification failed. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || !pendingRequest) return;

    setLoading(true);
    try {
      const captchaToken = await resolveCaptchaToken();
      if (!captchaToken) return;

      const response = await fetch(buildApiUrl(BOARD_REQUEST_RESEND_OTP_URL), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: pendingRequest.email,
          captchaToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          formatApiErrorMessage(data.message || "Failed to resend OTP"),
        );
      }

      applyOtpMeta(data);
      setOtp("");
      setOtpError("");
      setResendTimer(RESEND_COOLDOWN_SECONDS);
      otpInputRefs.current[0]?.focus();
      message.success(data.message || "OTP resent successfully.");
    } catch (error: any) {
      message.error(
        formatApiErrorMessage(
          error.message || "Failed to resend OTP. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setStep("form");
    setPendingRequest(null);
    setOtp("");
    setShowOtp(false);
    setOtpError("");
    setResendTimer(0);
    setRemainingAttempts(null);
    setExpiresIn(0);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      {/* Kept outside the modal so the invisible captcha widget stays mounted
          across the whole form -> OTP flow and never affects modal layout — a
          widget torn down with the form step would have nothing to execute()
          on the OTP step. */}
      <div ref={captchaRef} />

      <Modal
        title={
          step === "form" ? (
            <div>Apply for Exam Authority Access</div>
          ) : (
            <div>Verify Your Email</div>
          )
        }
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={step === "otp" ? 460 : 520}
        centered
        forceRender
        className={`request-access-modal${
          step === "otp" ? " request-access-modal--otp" : ""
        }`}
        maskClosable={false}
      >
        {step === "form" ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSendOtp}
            style={{ marginTop: "16px" }}
          >
            <Form.Item
              label="Organization Name"
              name="organizationName"
              rules={[
                { required: true, message: "Please enter your organization name" },
                {
                  min: 2,
                  message: "Organization name must be at least 2 characters",
                },
              ]}
            >
              <Input placeholder="Enter your organization name" size="large" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter your email address" size="large" />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phoneNumber"
              validateFirst
              rules={[
                { required: true, message: "Please enter your phone number" },
                {
                  validator: (_, value) => {
                    if (value && value.length < 12) {
                      return Promise.reject(
                        new Error("Please enter a valid phone number"),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <PhoneInputWrapper
                country={"in"}
                enableSearch={true}
                placeholder="Enter your phone number"
                containerClass="phone-input-container"
                inputClass="phone-input-field"
                buttonClass="phone-input-button"
                dropdownClass="phone-input-dropdown"
              />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[
                { required: true, message: "Please enter a description" },
                {
                  min: 10,
                  message: "Description must be at least 10 characters",
                },
              ]}
            >
              <Input.TextArea
                placeholder="Tell us about your organization"
                rows={4}
                size="large"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                className="submit-button"
              >
                Request Access
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div className="otp-verification-modal">
            <p className="otp-subtitle">
              Enter the 6-digit code sent to{" "}
              {pendingRequest?.email ? (
                <strong>{pendingRequest.email}</strong>
              ) : (
                "your email address"
              )}
            </p>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleVerifyOtp();
              }}
            >
              <div className="otp-inputs-section">
                <div className="otp-inputs-wrapper">
                  <div>
                    {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                      <input
                        key={index}
                        ref={(element) => {
                          otpInputRefs.current[index] = element;
                        }}
                        type={showOtp ? "tel" : "password"}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={1}
                        value={otp[index] || ""}
                        aria-label={`OTP digit ${index + 1}`}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          updateOtpDigit(index, event.target.value)
                        }
                        onKeyDown={(event) => handleOtpKeyDown(index, event)}
                        onPaste={handleOtpPaste}
                      />
                    ))}
                  </div>
                </div>

                <div className="otp-show-wrapper">
                  <button
                    type="button"
                    className="otp-show-link"
                    onClick={() => setShowOtp(!showOtp)}
                  >
                    {showOtp ? "Hide OTP" : "Show OTP"}
                  </button>
                </div>
              </div>

              {otpError && (
                <div className="otp-error-message">{otpError}</div>
              )}

              {(expiresIn > 0 || remainingAttempts !== null) && (
                <div className="otp-meta">
                  {expiresIn > 0 && (
                    <span>Code expires in {formatCountdown(expiresIn)}</span>
                  )}
                  {remainingAttempts !== null && (
                    <span>
                      {remainingAttempts}{" "}
                      {remainingAttempts === 1 ? "attempt" : "attempts"} left
                    </span>
                  )}
                </div>
              )}

              <div className="otp-resend-wrapper">
                <span className="otp-resend-text">
                  Didn&apos;t receive the code?{" "}
                  <button
                    type="button"
                    className="otp-resend-link"
                    onClick={handleResendOtp}
                    disabled={loading || resendTimer > 0}
                  >
                    {resendTimer > 0
                      ? `Resend in ${resendTimer}s`
                      : "Resend"}
                  </button>
                </span>
              </div>

              <div className="otp-actions">
                <button
                  type="submit"
                  className="otp-verify-button enabled"
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>

                <button
                  type="button"
                  className="otp-back-link"
                  onClick={handleBackToForm}
                  disabled={loading}
                >
                  Back to form
                </button>
              </div>
            </form>
          </div>
        )}
      </Modal>
    </>
  );
};

export default RequestAccessModal;
